import React, { useState, useEffect } from "react";
import { Building2, Mail, Settings, Power, Save, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { apiRequest, formatDate } from "../lib/utils";
import { toast } from "sonner";

export const BranchSettingsPage = () => {
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [branchData, setBranchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    if (selectedBranchId) {
      fetchBranchDetails(selectedBranchId);
      fetchEmailHistory(selectedBranchId);
    }
  }, [selectedBranchId]);

  const fetchBranches = async () => {
    try {
      const data = await apiRequest("/branches");
      setBranches(data);
      if (data.length > 0) {
        setSelectedBranchId(data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch branches");
    } finally {
      setLoading(false);
    }
  };

  const fetchBranchDetails = async (branchId) => {
    try {
      const data = await apiRequest(`/branches/${branchId}`);
      setBranchData(data);
    } catch (error) {
      console.error("Failed to fetch branch details:", error);
    }
  };

  const fetchEmailHistory = async (branchId) => {
    try {
      const data = await apiRequest(`/reports/monthly-email/history?branch_id=${branchId}`);
      setEmailHistory(data);
    } catch (error) {
      console.error("Failed to fetch email history:", error);
    }
  };

  const handleSave = async () => {
    if (!branchData) return;
    setSaving(true);
    try {
      await apiRequest(`/branches/${selectedBranchId}`, {
        method: "PUT",
        body: JSON.stringify({
          name: branchData.name,
          email: branchData.email,
          sdr_active: branchData.sdr_active,
          monthly_email_active: branchData.monthly_email_active
        })
      });
      toast.success("Branch settings saved successfully");
    } catch (error) {
      toast.error("Failed to save branch settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSendMonthlyReport = async () => {
    if (!selectedBranchId) return;
    try {
      const result = await apiRequest("/reports/monthly-email/send", {
        method: "POST",
        body: JSON.stringify({ branch_id: selectedBranchId })
      });
      toast.success(result.message);
      fetchEmailHistory(selectedBranchId);
    } catch (error) {
      toast.error(error.message || "Failed to send monthly report");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branch-settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Branch Settings</h1>
          <p className="text-muted-foreground">Configure branch-specific features and automations</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedBranchId} onValueChange={setSelectedBranchId}>
            <SelectTrigger className="w-[200px]" data-testid="branch-selector">
              <SelectValue placeholder="Select branch" />
            </SelectTrigger>
            <SelectContent>
              {branches.map((branch) => (
                <SelectItem key={branch.id} value={branch.id}>
                  {branch.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={handleSave} disabled={saving || !branchData} data-testid="save-settings-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {branchData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* SDR Operations Toggle */}
          <Card className="bento-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Power className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">SDR Operations</CardTitle>
                  <CardDescription>Control Due/Overdue workflow for this branch</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                <div>
                  <p className="font-medium">Enable SDR Workflow</p>
                  <p className="text-sm text-muted-foreground">
                    When enabled, Due/Overdue/Delayed/No Show customers will be visible and assigned to CREs
                  </p>
                </div>
                <Switch
                  checked={branchData.sdr_active !== false}
                  onCheckedChange={(checked) => setBranchData({ ...branchData, sdr_active: checked })}
                  data-testid="sdr-active-toggle"
                />
              </div>
              <div className="p-3 rounded-lg border border-dashed">
                <p className="text-sm text-muted-foreground">
                  <strong>Current Status:</strong>{" "}
                  {branchData.sdr_active !== false ? (
                    <Badge className="bg-green-100 text-green-800 ml-2">Active</Badge>
                  ) : (
                    <Badge variant="secondary" className="ml-2">Disabled</Badge>
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Disabling SDR operations will hide all Due/Overdue lists for this branch.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Email Reports */}
          <Card className="bento-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100">
                  <Mail className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Monthly Email Reports</CardTitle>
                  <CardDescription>Automated performance reports sent to branch email</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                <div>
                  <p className="font-medium">Enable Monthly Reports</p>
                  <p className="text-sm text-muted-foreground">
                    Send automated performance summary on the 1st of each month
                  </p>
                </div>
                <Switch
                  checked={branchData.monthly_email_active === true}
                  onCheckedChange={(checked) => setBranchData({ ...branchData, monthly_email_active: checked })}
                  data-testid="monthly-email-toggle"
                />
              </div>
              <div>
                <Label htmlFor="branch-email">Branch Email Address</Label>
                <Input
                  id="branch-email"
                  type="email"
                  placeholder="branch@example.com"
                  value={branchData.email || ""}
                  onChange={(e) => setBranchData({ ...branchData, email: e.target.value })}
                  className="mt-1"
                  data-testid="branch-email-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Monthly reports will be sent to this email address
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleSendMonthlyReport}
                disabled={!branchData.email || !branchData.monthly_email_active}
                className="w-full"
                data-testid="send-report-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Send Report Now (Test)
              </Button>
            </CardContent>
          </Card>

          {/* Branch Info */}
          <Card className="bento-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100">
                  <Building2 className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Branch Information</CardTitle>
                  <CardDescription>Basic branch details</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="branch-name">Branch Name</Label>
                <Input
                  id="branch-name"
                  value={branchData.name || ""}
                  onChange={(e) => setBranchData({ ...branchData, name: e.target.value })}
                  className="mt-1"
                  data-testid="branch-name-input"
                />
              </div>
              <div>
                <Label htmlFor="branch-phone">Phone</Label>
                <Input
                  id="branch-phone"
                  value={branchData.phone || ""}
                  onChange={(e) => setBranchData({ ...branchData, phone: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="branch-address">Address</Label>
                <Input
                  id="branch-address"
                  value={branchData.address || ""}
                  onChange={(e) => setBranchData({ ...branchData, address: e.target.value })}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* Email Report History */}
          <Card className="bento-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gray-100">
                  <Settings className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Report History</CardTitle>
                  <CardDescription>Previously sent monthly reports</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {emailHistory.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No reports sent yet
                </p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {emailHistory.map((report) => (
                    <div
                      key={report.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                    >
                      <div>
                        <p className="text-sm font-medium">{report.report_month}</p>
                        <p className="text-xs text-muted-foreground">
                          Sent to {report.branch_email}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge variant={report.status === "sent" ? "default" : "secondary"}>
                          {report.status}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(report.sent_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BranchSettingsPage;
